import {
  Component,
  ComponentFactoryResolver,
  OnDestroy,
  OnInit
} from '@angular/core';
import { BehaviorSubject, interval, of, Subject } from 'rxjs';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { AuthService } from '@ml/auth/services';
import { AppBarService } from '@ml/shared/services/app-bar.service';
import strings from '../../../../assets/i18n/strings';
import { SidenavOption } from '@ml/core/types/interfaces/sidenav-option.interface';
import { User } from '@ml/core/types/classes/user.class';
import { SidenavService, UserService } from '@ml/core/services';
import { UserRole } from '@ml/core/types/enums';
import { NotificationsComponent } from '@ml/notifications/containers';
import { NotificationsService } from '@ml/notifications/services';
import {
  catchError,
  distinctUntilChanged,
  filter,
  first,
  map,
  startWith,
  switchMap,
  takeUntil
} from 'rxjs/operators';
import { ConnectionService } from '@ml/core/services/connection.service';
import { TaskLogsService } from '@ml/tasks/services';
import { PushNotificationsService } from '@ml/core/services/push-notifications.service';

@Component({
  selector: 'ml-main-layout',
  template: `
    <ml-right-sidenav>
      <div class="main-layout layout-row flex">
        <ml-sidenav
          fxShow.lg
          fxHide
          [active]="active"
          [sidenavOptions]="sidenavOptions"
          [user]="user"
        ></ml-sidenav>
        <div class="main-container layout-column flex">
          <ml-app-bar
            [user]="user"
            [connection]="connection"
            [title]="tabBarTitle"
            [sidenavOptions]="sidenavOptions"
            [unreadNotifications]="unreadNotifications"
            (logout)="onLogOut()"
            (openNotifications)="onOpenNotifications()"
          ></ml-app-bar>
          <section class="main-content">
            <div class="content-container">
              <div class="content">
                <div *ngIf="!connection" class="no-connection">
                  {{ 'no-connection' | translate }}
                </div>
                <router-outlet></router-outlet>
              </div>
            </div>
          </section>
          <div *ngIf="isCustomer" class="contact-information">
            <ml-administrator-contact-information></ml-administrator-contact-information>
          </div>
        </div>
      </div>
    </ml-right-sidenav>
  `,
  styleUrls: ['./main-layout.component.scss']
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  currentRoute$: BehaviorSubject<ActivatedRouteSnapshot>;
  tabBarTitle: string;
  sidenavOptions: SidenavOption[];
  active: string;
  user: User;
  unreadNotifications: boolean;
  connection = true;

  private ngUnsubscribe = new Subject();

  readonly VAPID_PUBLIC_KEY =
    'BBrWw53KJ7FMEBJ4v0kxJmsWQjS7dS_EGlY6LpBFl2JeBv3QV68axZZrRsZ4stjqh9mJw5cq0XGt3g51QpRNslY';

  constructor(
    private router: Router,
    private authService: AuthService,
    private appBarService: AppBarService,
    private userService: UserService,
    private sidenavService: SidenavService,
    private componentFactoryResolver: ComponentFactoryResolver,
    private notificationsService: NotificationsService,
    private connectionService: ConnectionService,
    private taskLogsService: TaskLogsService,
    private pushNotificationsService: PushNotificationsService
  ) {}

  ngOnInit() {
    this.initNotificationsPooling();

    this.connectionService.connection$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(value => (this.connection = value));

    this.connectionService.connection$
      .pipe(
        distinctUntilChanged(),
        filter(value => value),
        switchMap(() => this.taskLogsService.submitCachedLogs()),
        switchMap(() => this.taskLogsService.list())
      )
      .subscribe(value => {});

    this.user = this.userService.user;

    this.initSidebarOptions();
    this.currentRoute$ = new BehaviorSubject(
      this.router.routerState.root.snapshot
    );
    this.setAppBarTitle();

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.setAppBarTitle();
        this.currentRoute$.next(this.router.routerState.root.snapshot);
      }
    });

    this.pushNotificationsService
      .subscribeChangeToken$()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe();

    this.pushNotificationsService
      .requestPermission$()
      .pipe(first())
      .subscribe();
  }

  onLogOut() {
    this.userService.setFCMToken(null).subscribe(() => {
      this.authService.logout();
      this.router.navigate(['auth']);
    });
  }

  onOpenNotifications() {
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(
      NotificationsComponent
    );
    this.unreadNotifications = false;
    this.sidenavService.open(componentFactory);
  }

  private initNotificationsPooling() {
    const poolingInterval = 150000;
    interval(poolingInterval)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        startWith(1),
        filter(() => this.connectionService.connection),
        switchMap(() => this.notificationsService.unreadNotifications()),
        catchError(err => of(null)),
        map(value => value || 0)
      )
      .subscribe((value: number) => (this.unreadNotifications = value > 0));
  }

  private setAppBarTitle() {
    const url = this.router.url;
    this.active = url.split('/')[1].split('?')[0];
    const option = this.sidenavOptions.find(
      value => value.routerLink === this.active
    );
    this.appBarService.titleAppBar.subscribe(value => {
      if (value) {
        this.tabBarTitle = value;
      } else {
        this.tabBarTitle = option ? option.name : 'Dashboard';
      }
    });
  }

  private initSidebarOptions() {
    const options = [
      {
        name: strings.orders,
        icon: 'assignment',
        routerLink: 'orders',
        role: [UserRole.ADMIN]
      },
      {
        name: strings.customers,
        icon: 'domain',
        routerLink: 'customers',
        role: [UserRole.ADMIN]
      },
      {
        name: 'suppliers',
        icon: 'archive',
        routerLink: 'suppliers',
        role: [UserRole.ADMIN]
      },
      {
        name: strings.machinery,
        icon: 'build',
        routerLink: 'machineries',
        role: [UserRole.ADMIN]
      },
      {
        name: strings.services,
        icon: 'settings',
        routerLink: 'services',
        role: [UserRole.ADMIN]
      },
      {
        name: 'tasks',
        icon: 'assignment_turned_in',
        routerLink: 'tasks',
        role: [UserRole.ADMIN, UserRole.EMPLOYEE]
      },
      {
        name: strings.accounting,
        icon: 'insert_chart',
        routerLink: 'accounting',
        role: [UserRole.ADMIN]
      },
      {
        name: strings.employees,
        icon: 'people',
        routerLink: 'employees',
        role: [UserRole.ADMIN]
      },
      {
        name: 'salaries',
        icon: 'salaries',
        routerLink: 'salaries',
        role: [UserRole.ADMIN, UserRole.EMPLOYEE]
      },
      {
        name: strings.orders,
        icon: 'assignment',
        routerLink: 'customer-orders',
        role: [UserRole.CUSTOMER]
      },
      {
        name: 'locations',
        icon: 'location',
        routerLink: 'locations',
        role: [UserRole.CUSTOMER]
      }
    ];
    this.sidenavOptions = options.filter(
      option => option.role.indexOf(this.user.role) >= 0
    );
  }

  get isCustomer() {
    return this.user.role === UserRole.CUSTOMER;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
